/**
 * ProjectPortalSection — inline portal management on the project detail page.
 *
 * Shows portal state directly in the project page:
 * - Empty state with "Share with Client" button when no portal exists
 * - Portal link (copy), member list, preview button, welcome message editor when portal exists
 *
 * Feature-gated: only rendered when hasPortalManagement is true.
 * planTEK — Portal Management improvements.
 */
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check, ExternalLink, Users, Send, RefreshCw, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { isValidEmail } from '@/lib/email-validation';
import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Member {
  id: string;
  member_email: string;
  role: string;
  status: 'pending' | 'active' | 'revoked';
  invited_by: string;
  created_at: string;
}

export interface ProjectPortalSectionProps {
  projectId: string;
  projectName: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: Member['status']) {
  if (status === 'active') return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
  if (status === 'pending') return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
  return <Badge variant="outline" className="text-gray-500">Revoked</Badge>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ProjectPortalSection({ projectId, projectName }: ProjectPortalSectionProps) {
  const queryClient = useQueryClient();

  // Portal data
  const { data: shareData, isLoading, refetch } = useQuery({
    queryKey: ['project-share', projectId],
    queryFn: () => api.projectShare.getForProject(projectId),
    staleTime: 30_000,
  });

  // Invite form state
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Per-member action state
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Welcome message state
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const [isEditingWelcome, setIsEditingWelcome] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);

  const view = shareData?.view ?? null;
  const shareUrl = shareData?.share_url ?? null;
  const portalUrlBase = shareData?.portal_url ?? (view ? `${window.location.origin}/portal/${view.token}` : null);
  const portalUrl = portalUrlBase;
  const previewUrl = portalUrlBase ? `${portalUrlBase}?preview=true` : null;
  const members: Member[] = (shareData?.members ?? []).filter((m: Member) => m.status !== 'revoked');

  // Sync welcome message from server data when view loads or changes
  useEffect(() => {
    if (view && !isEditingWelcome) {
      setWelcomeMessage(view.welcome_message ?? '');
    }
  }, [view?.welcome_message]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleCopyLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleSendInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) { toast.error('Email is required'); return; }
    if (!isValidEmail(trimmed)) { toast.error('Please enter a valid email address'); return; }

    setSending(true);
    try {
      await api.projectShare.share(projectId, { emails: [trimmed], template: 'standard' });
      toast.success(`Invite sent to ${trimmed}`);
      setEmail('');
      queryClient.invalidateQueries({ queryKey: ['project-share', projectId] });
      queryClient.invalidateQueries({ queryKey: ['external-views'] });
    } catch (err: any) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (member: Member) => {
    if (!view?.id) return;
    setRevokingId(member.id);
    try {
      await api.projectShare.revokeMember(view.id, member.id);
      toast.success(`Access revoked for ${member.member_email}`);
      queryClient.invalidateQueries({ queryKey: ['project-share', projectId] });
    } catch (err: any) {
      toast.error(err.message || 'Failed to revoke access');
    } finally {
      setRevokingId(null);
    }
  };

  const handleResend = async (member: Member) => {
    if (!view?.id) return;
    setResendingId(member.id);
    try {
      await api.projectShare.resendInvite(view.id, { email: member.member_email });
      toast.success(`Invitation resent to ${member.member_email}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  const handleSaveWelcome = async () => {
    if (!view?.id) return;
    setSavingWelcome(true);
    try {
      await api.externalViews.update(view.id, { welcome_message: welcomeMessage || null });
      toast.success('Welcome message saved');
      setIsEditingWelcome(false);
      queryClient.invalidateQueries({ queryKey: ['project-share', projectId] });
    } catch (err: any) {
      toast.error(err.message || 'Failed to save welcome message');
    } finally {
      setSavingWelcome(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        Loading portal state...
      </div>
    );
  }

  // ── Empty state — no portal exists yet ──────────────────────────────────────

  if (!view) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center space-y-4" data-testid="portal-empty-state">
        <Globe className="h-8 w-8 text-muted-foreground mx-auto" />
        <div>
          <p className="text-sm font-medium">No client portal yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Share this project with a client to create a portal. They'll get a secure link to view project updates.
          </p>
        </div>

        <div className="max-w-sm mx-auto space-y-3">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSendInvite(); }}
              data-testid="portal-invite-email"
            />
            <Button
              onClick={handleSendInvite}
              disabled={sending || !email.trim()}
              data-testid="portal-send-invite"
            >
              {sending ? 'Sending...' : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Share
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Active portal ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5" data-testid="portal-section">
      {/* Portal link. Iter3 B11: the raw UUID token is engineering-flavored
       *  noise on the project detail page; mask it as `…1c81` so the URL stays
       *  copy-paste accurate (clipboard still grabs the full token via
       *  handleCopyLink) while the visible chrome reads as a clean address. */}
      <div className="rounded-md border bg-muted/40 px-3 py-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Portal Link</p>
        <div className="flex items-center gap-2">
          <p className="text-sm text-foreground truncate flex-1 font-mono" data-testid="portal-share-url">
            {portalUrl && portalUrl.includes('/portal/')
              ? (() => {
                  const [host, token = ''] = portalUrl.split('/portal/');
                  const masked = token.length > 8
                    ? `${token.slice(0, 4)}…${token.slice(-4)}`
                    : token;
                  return `${host}/portal/${masked}`;
                })()
              : portalUrl}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="h-7 shrink-0"
            onClick={handleCopyLink}
            data-testid="portal-copy-link"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center h-7 px-2 rounded-md border text-xs hover:bg-muted transition-colors shrink-0"
              title="Preview portal"
              data-testid="portal-preview-link"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Members */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium flex items-center gap-1.5">
            <Users className="h-4 w-4 text-muted-foreground" />
            People with access
            {members.length > 0 && (
              <Badge variant="secondary" className="ml-1">{members.length}</Badge>
            )}
          </p>
        </div>

        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">No one has access yet.</p>
        ) : (
          <div className="space-y-1.5" data-testid="portal-members-list">
            {members.map(member => (
              <div
                key={member.id}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                data-testid={`portal-member-row-${member.member_email}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate">{member.member_email}</span>
                  {statusBadge(member.status)}
                </div>
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  {member.status === 'active' && portalUrlBase && (
                    <a
                      href={`${portalUrlBase}?preview=true&as=${encodeURIComponent(member.member_email)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs hover:bg-muted transition-colors"
                      title={`Preview portal as ${member.member_email}`}
                      data-testid={`portal-preview-as-${member.member_email}`}
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="hidden sm:inline">Preview as {member.member_email}</span>
                    </a>
                  )}
                  {member.status === 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => handleResend(member)}
                      disabled={resendingId === member.id}
                      title="Resend invite"
                      data-testid={`portal-resend-${member.member_email}`}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => handleRevoke(member)}
                    disabled={revokingId === member.id}
                    title="Revoke access"
                    data-testid={`portal-revoke-${member.member_email}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview portal */}
      {previewUrl && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Preview portal</p>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs hover:bg-muted transition-colors"
            title="Opens the portal view that your clients will see"
            data-testid="portal-preview-button"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Preview Portal
          </a>
        </div>
      )}

      {/* Add another person */}
      <div className="space-y-2">
        <Label htmlFor="portal-invite-email-active">Add client</Label>
        <div className="flex gap-2">
          <Input
            id="portal-invite-email-active"
            type="email"
            placeholder="client@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSendInvite(); }}
            data-testid="portal-invite-email"
          />
          <Button
            onClick={handleSendInvite}
            disabled={sending || !email.trim()}
            size="sm"
            data-testid="portal-send-invite"
          >
            {sending ? 'Sending...' : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Welcome message */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="portal-welcome-message">Welcome message</Label>
          {!isEditingWelcome && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                setWelcomeMessage(view.welcome_message ?? '');
                setIsEditingWelcome(true);
              }}
              data-testid="portal-edit-welcome"
            >
              Edit
            </Button>
          )}
        </div>
        {isEditingWelcome ? (
          <div className="space-y-2">
            <Textarea
              id="portal-welcome-message"
              value={welcomeMessage}
              onChange={e => setWelcomeMessage(e.target.value)}
              placeholder="Welcome to your project portal! Feel free to review updates and reach out with any questions."
              className="resize-none text-sm"
              rows={3}
              maxLength={1000}
              data-testid="portal-welcome-textarea"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditingWelcome(false);
                  setWelcomeMessage(view.welcome_message ?? '');
                }}
                disabled={savingWelcome}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveWelcome}
                disabled={savingWelcome}
                data-testid="portal-save-welcome"
              >
                {savingWelcome ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground min-h-[2.5rem]" data-testid="portal-welcome-display">
            {view.welcome_message || 'No welcome message set.'}
          </p>
        )}
      </div>
    </div>
  );
}
